import React, { memo, useRef, useEffect } from 'react';
import { useStoreApi } from '@xyflow/react';

interface InverseScaleBoxProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  scaleOrigin?: string;
}

/**
 * 反向缩放容器 — 让子元素在画布缩放时保持视觉尺寸不变
 *
 * 原理：用 transform: scale(1/zoom) 抵消父级 viewport 的 scale(zoom)
 *
 * 订阅源：ReactFlow 的 store（transform[2] 是实时 zoom）
 * 之前订阅 useCanvasStore.zoom，但 useCanvasStore 通过 60ms debounce 更新
 * → 缩放画布时顶部栏/底部面板滞后 60ms 才更新反向缩放
 * 现在直接订阅 ReactFlow store，每帧实时响应画布缩放
 *
 * 性能：直接操作 DOM（el.style.transform），不触发 React 重渲染
 */
export const InverseScaleBox: React.FC<InverseScaleBoxProps> = memo(({ children, className, style, scaleOrigin }) => {
  const ref = useRef<HTMLDivElement>(null);
  const baseTransform = style?.transform || '';
  const store = useStoreApi();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const applyZoom = (zoom: number) => {
      el.style.transform = `${baseTransform} scale(${1 / zoom})`;
    };

    // 初始值
    applyZoom(store.getState().transform[2]);

    // 订阅 ReactFlow store 的 transform 变化（实时，无 debounce）
    let prevZoom = store.getState().transform[2];
    const unsub = store.subscribe((state) => {
      const zoom = state.transform[2];
      if (zoom !== prevZoom) {
        prevZoom = zoom;
        applyZoom(zoom);
      }
    });

    return unsub;
  }, [baseTransform, store]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ ...style, transformOrigin: scaleOrigin || style?.transformOrigin }}
    >
      {children}
    </div>
  );
});
